import React, { useState } from 'react'
import styled from '@emotion/styled'
import { withTheme } from 'emotion-theming'
import { array, object, string } from 'prop-types'

// Import Components
import { BMonogram } from '@jelly/components'
import { Link } from '@jelly/ui'

// Styled components
const MethodologyWrapper = styled.div`
	height: 100%;
	max-width: 100%;
	position: relative;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		max-width: 50%;
		max-height: 600px;
		min-height: 600px;
	}
	@media (min-width: 768px) and (max-width: 995px) {
		max-height: 750px;
		min-height: 750px;
	}
`
const IconWrapper = styled.div`
	display: none;
	position: relative;
	z-index: -1;

	${({ theme }) => theme.MQ.M} {
		margin-top: ${({ showMore }) => (showMore ? '15%' : '10%')};
		text-align: center;
		width: 40%;
		display: block;
		transition: all 0.5s ease;
	}
	@media (min-width: 768px) and (max-width: 950px) {
		margin-top: ${({ showMore }) => (showMore ? '45%' : '40%')};
	}
	@media (min-width: 950px) and (max-width: 1300px) {
		padding: 0px 20px 0px 20px;
		margin-top: ${({ showMore }) => (showMore ? '25%' : '20%')};
	}
	@media (min-width: 2000px) and (max-width: 3000px) {
		margin-top: ${({ showMore }) => (showMore ? '106px' : '0%')};
	}
`
const WrapperSub = styled.div`
	background-color: #ffffff;
	text-align: left;
	padding: 40px;
	max-width: 80%;

	${({ theme }) => theme.MQ.M} {
		padding: 0px 20px 0px 20px;
		max-height: 600px;
		top: ${({ showMore }) => (showMore ? '10%' : '50%')};
		position: absolute;
		transition: all 0.5s ease;
	}
	@media (min-width: 768px) and (max-width: 1079px) {
		padding: 0px 20px 0px 20px;
		top: ${({ showMore }) => (showMore ? '5%' : '40%')};
	}
	@media (min-width: 1740px) and (max-width: 2000px) {
		top: ${({ showMore }) => (showMore ? '20%' : '60%')};
	}
	@media (min-width: 2000px) and (max-width: 3000px) {
		top: ${({ showMore }) => (showMore ? '20%' : '55%')};
	}
`
const Title = styled.div`
	${({ theme }) => theme.header.h2};
	color: ${({ theme }) => theme.colors.navy};
	margin: 0;
	${({ theme }) => theme.MQ.M} {
		font-size: 34px;
	}
`
const Description = styled.p`
	${({ theme }) => theme.text.bodyLarge};
	color: ${({ theme }) => theme.colors.darkGrey};
	margin: 0;

	${({ theme }) => theme.MQ.M} {
		padding-bottom: 20px;
	}
	${({ theme }) => theme.MQ.L} {
		height: 80px;
	}
	${({ theme }) => theme.MQ.XL} {
		max-width: 90%;
		padding-bottom: 0;
	}
`
const ReadButton = styled(Link)`
	${({ theme }) => theme.text.sectionHeaderSmall};
	color: ${({ theme }) => theme.colors.orange};
	padding-top: 10px;
	text-align: left;
	display: block;
	cursor: pointer;

	&:hover {
		color: ${({ theme }) => theme.colors.navy};
		.lessView {
			display: none;
		}
		.methodologyPanel {
			display: block;
		}
	}
`
const MethodologyDescription = styled.div`
	padding-top: 10px;
	font-size: 18px;
	color: ${({ theme }) => theme.colors.darkGrey};
	transition: all 0.5s ease;
	max-height: ${({ showMore }) => (showMore ? '480px' : '0')};
	overflow: hidden;
	${({ theme }) => theme.MQ.M} {
		${({ theme }) => theme.text.bodyLarge};
		max-height: ${({ showMore }) => (showMore ? '280px' : '0')};
	}
	${({ theme }) => theme.MQ.XL} {
		max-width: 90%;
	}
	@media (min-width: 768px) and (max-width: 1090px) {
		max-height: ${({ showMore }) => (showMore ? '420px' : '0')};
		padding-top: 0;
	}
	@media (min-width: 1950px) and (max-width: 3000px) {
		max-height: ${({ showMore }) => (showMore ? '280px' : '0')};
	}
`
const DescriptionList = styled.ul`
	margin: 0;
	line-height: 1.4em;
	padding-inline-start: 20px;
`
const DescriptionPoint = styled.li`
	padding-bottom: 20px;
`
const WrapperOuter = styled.div`
	${({ theme }) => theme.MQ.M} {
		padding: 40px 50px;
	}
`
Methodology.propTypes = {
	description: string.isRequired,
	methodologyDescriptionPoints: array.isRequired,
	theme: object.isRequired,
	title: string.isRequired,
}

/**
 * This is the methodology component for the review landing page
 * Contains methodology content which expands with a transition
 */
function Methodology({ description, methodologyDescriptionPoints, theme, title }) {
	const [showMore, setShouldShowMore] = useState(false)
	const handleReadButton = () => {
		setShouldShowMore(!showMore)
	}
	return (
		<MethodologyWrapper showMore={showMore} className="methodologyShadow">
			<WrapperOuter>
				<IconWrapper showMore={showMore}>
					<BMonogram fill={theme.colors.lightGrey} width="60%" />
				</IconWrapper>
				<WrapperSub showMore={showMore}>
					<Title>{title}</Title>
					<Description>{description}</Description>
					<MethodologyDescription showMore={showMore}>
						<DescriptionList>
							{methodologyDescriptionPoints.map((point, i) => (
								<DescriptionPoint key={i}>{point}</DescriptionPoint>
							))}
						</DescriptionList>
					</MethodologyDescription>
					<ReadButton onClick={handleReadButton}>{showMore ? 'Read Less' : ' Read More'}</ReadButton>
				</WrapperSub>
			</WrapperOuter>
		</MethodologyWrapper>
	)
}
export default withTheme(Methodology)
